import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  DefaultButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  PrimaryButton,
  Theme,
  makeStyles,
  createStyles,
} from '@bb-ui/react-library';
import { Banner } from '@bb-ui/react-library/dist/components/Banner';
import { DialogProps } from '@bb-ui/react-library/dist/components/Dialog/Dialog.types';
import { TenantData, TenantTypes } from 'App.types';
import { TextField } from '@bb-ui/react-library/dist/components/TextField';
import { useAppConfigContext } from 'contexts/AppConfigContext';
import { Warning } from '@bb-ui/icons/dist/small/Warning';

const EXCLUDED_TENANT_TYPES = ['ReleaseStage', 'LearnDms', 'Illuminate'];

const styles = (theme: Theme) =>
  createStyles({
    editTenantInput: {
      margin: theme.spacing(2, 0, 1),
    },
    regionWarningIcon: {
      flexShrink: 0,
      marginTop: 3,
      marginRight: theme.spacing(1),
      marginLeft: theme.spacing(-3),
    },
  });

const useStyles = makeStyles(styles);

export interface EditTenantDialogProps extends Omit<DialogProps, 'onSubmit'> {
  onClose: () => void;
  onSave: ({
    parentTenant,
    description,
    hostname,
    region,
    tenantType,
  }: Partial<TenantData>) => void;
  tenant?: Partial<TenantData>;
}

export const EditTenantDialog = (props: EditTenantDialogProps) => {
  const { id, open, onSave, onClose, tenant } = props;
  const [parentTenant, setParentTenant] = React.useState<string>();
  const [description, setDescription] = React.useState<string>();
  const [hostname, setHostName] = React.useState<string>();
  const [region, setRegion] = React.useState<string>();
  const [tenantType, setTenantType] = React.useState<string>();
  const { t } = useTranslation();
  const classes = useStyles(props);
  const supportedRegions = useAppConfigContext().tenantSupportedRegions ?? [];

  const regionChanged = region !== undefined && region !== (tenant?.region ?? '');

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSave({ parentTenant, description, hostname, region, tenantType });
  }

  function handleTenantType(event: any) {
    setTenantType(event.target.value);
  }

  function handleRegion(event: any) {
    setRegion(event.target.value);
  }

  React.useEffect(() => {
    if (open) {
      // Reset on open
      setParentTenant(tenant?.parentTenant);
      setDescription(tenant?.description);
      setHostName(tenant?.hostname);
      setRegion(tenant?.region);
      setTenantType(tenant?.tenantType);
    }
  }, [open, tenant]);

  return (
    <Dialog id={id} open={open} onClose={onClose} aria-labelledby={`${id}-title`} maxWidth="md">
      <DialogTitle id={`${id}-title`} onClose={onClose} closeLabel={t('global.close')}>
        {t('tenantInformation.pageTitle')}
      </DialogTitle>
      <form onSubmit={handleSubmit} data-testid="tenant-form">
        <DialogContent>
          <TextField
            className={classes.editTenantInput}
            id="edit-tenant-parent-input"
            label={t('tenantInformation.parentTenantId')}
            onChange={(event) => setParentTenant(event.target.value)}
            placeholder={t('tenantInformation.parentTenantId')}
            defaultValue={tenant?.parentTenant ?? ''}
            fullWidth
            autoFocus
          />
          <TextField
            className={classes.editTenantInput}
            id="edit-tenant-description-input"
            label={t('tenantInformation.description')}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={t('tenantInformation.description')}
            defaultValue={tenant?.description ?? ''}
            fullWidth
          />
          <TextField
            className={classes.editTenantInput}
            id="edit-tenant-hostname-input"
            label={t('tenantInformation.hostname')}
            onChange={(event) => setHostName(event.target.value)}
            placeholder={t('tenantInformation.hostname')}
            defaultValue={tenant?.hostname ?? ''}
            fullWidth
          />
          <TextField
            className={classes.editTenantInput}
            id="edit-tenant-region-input"
            select
            value={region ?? ''}
            label={t('tenantInformation.region')}
            onChange={handleRegion}
            placeholder={t('tenantInformation.region')}
            fullWidth
          >
            {supportedRegions.map((option) => (
              <MenuItem onClick={handleRegion} key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          {regionChanged && (
            <Banner severity="warning">
              <Warning className={classes.regionWarningIcon} />
              {t('tenantInformation.regionChangeWarning')}
            </Banner>
          )}
          <TextField
            className={classes.editTenantInput}
            id="edit-tenant-type-input"
            select
            label={t('tenantInformation.tenantType')}
            onChange={handleTenantType}
            placeholder={t('tenantInformation.tenantType')}
            value={tenantType ?? ''}
            fullWidth
          >
            {TenantTypes.filter((option) => !EXCLUDED_TENANT_TYPES.includes(option)).map(
              (option) => (
                <MenuItem onClick={handleTenantType} key={option} value={option}>
                  {t(`tenantTypes.${option.toLowerCase()}`)}
                </MenuItem>
              ),
            )}
          </TextField>
        </DialogContent>
        <DialogActions>
          <DefaultButton onClick={onClose}>{t('global.cancel')}</DefaultButton>
          <PrimaryButton type="submit">{t('global.save')}</PrimaryButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
